import { useCallback, useState } from 'react';
import { useGetJobOffers } from '@modules/JobOffers/hooks/useGetJobOffers';
import { defaultFilters, IJobOfferFilter, JobOfferFilter } from '@modules/JobOffers/JobOfferFilter';
import { usePageSize } from '@hooks/usePageSize';
import JobOfferList from '@modules/JobOffers/JobOfferList';
import { isDateRange, isNumberRange } from '@components/Filter/filter.utils';
import { isArray, isObject } from 'lodash';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useOutletContext, useParams } from 'react-router-dom';

interface OutletContextType {
    searchParams: any;
    setSearchParams: (params: any) => void;
}

const ContainerWrapper = styled(Box)(({ theme }) => ({
    borderRadius: '24px',
    background: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    flex: 1,
}));

const BrandJobOffers = () => {
    const { id } = useParams<{ id: string }>();
    useOutletContext<OutletContextType>();

    const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
    const [selectedId, setSelectedId] = useState('');
    const [query, setQuery] = useState('');
    const { pageSize, storePageSize } = usePageSize({
        defaultPageSize: 12,
        storageKey: 'brand-job-offers-page-size',
    });
    const [pagination, setPagination] = useState({ page: 1, limit: pageSize });
    const [sortBy, setSortBy] = useState({ field: 'createdDate', order: -1 });
    const [filter, setFilter] = useState<IJobOfferFilter>(defaultFilters);

    const changeViewMode = (event: React.MouseEvent<HTMLButtonElement>) => {
        setViewMode(event.currentTarget.value as 'grid' | 'list');
    };

    const onQueryChange = useCallback((value: string) => {
        setQuery(value);
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    const onSortChange = useCallback((newSortBy: { field: string; order: number }) => {
        setSortBy(newSortBy);
    }, []);

    const onFilterChange = useCallback((newFilter: IJobOfferFilter) => {
        setFilter(newFilter);
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    const transformedFilter = Object.keys(filter).reduce((acc, key) => {
        const value = filter[key];
        if (key === 'jobApplicationStatus') {
            acc[key] = value.map(item => ({ status: item.group, subStatus: item.value }));
        } else if (key === 'specialties') {
            acc[key] = value?.map(item => item.value);
        } else if (isDateRange(value)) {
            acc[key] = { start: value?.start || null, end: value?.end || null };
        } else if (isNumberRange(value)) {
            if (value?.min && value?.max) {
                acc[key] = { min: parseFloat(value?.min), max: parseFloat(value?.max) };
            }
        } else if (isObject(value) && 'value' in value) {
            acc[key] = value.value;
        } else if (isArray(value)) {
            if (value && value?.length > 0) {
                acc[key] = value.map(item => (item?.value ? item.value : item));
            }
        } else {
            if (value) {
                acc[key] = value;
            }
        }
        return acc;
    }, {} as any);

    // Always lock brandId to the current brand
    transformedFilter.brandId = id ? [id] : [];

    const { jobOffers, totalDocs, totalPages, loading, error } = useGetJobOffers(
        query,
        transformedFilter,
        sortBy.field,
        pagination.page - 1,
        pagination.limit,
    );

    if (error) {
        return <div>Erreur lors du chargement des offres d'emploi.</div>;
    }

    return (
        <ContainerWrapper>
            <JobOfferFilter
                viewMode={viewMode}
                changeViewMode={changeViewMode}
                totalDocs={totalDocs}
                onQueryChange={onQueryChange}
                onSortChange={onSortChange}
                onFilterChange={onFilterChange}
                filter={filter}
                query={query}
                brandId={id}
            />
            <JobOfferList
                viewMode={viewMode}
                data={jobOffers}
                onConfirm={() => {}}
                loading={loading}
                setSelectedId={setSelectedId}
                currentPage={pagination.page}
                pageSize={pagination.limit}
                totalRows={totalDocs}
                onPageChange={page => setPagination(prev => ({ ...prev, page }))}
                onPageSizeChange={limit => {
                    storePageSize(limit);
                    setPagination({ page: 1, limit });
                }}
            />
        </ContainerWrapper>
    );
};

export default BrandJobOffers;
